import { Box, Button, CircularProgress, Drawer, Grid, TextField } from '@mui/material';
import { styled } from "@mui/material/styles";
import { DrawerHeader, Ico_Delete, PageTitle, useSnackbar } from '@sagecentral/core-ui';
import useCloneWorkItem from 'hooks/tasks/useCloneWorkItem';
import { useEffect, useState } from 'react';

type WorkItemCloneProps = {
    workItemId: number;
    workItemName: string;
    openCloneWorkitem: boolean;
    setOpenCloneWorkitem: React.Dispatch<React.SetStateAction<boolean>>;
};

const KeyGrid = styled(Grid)({
    fontWeight: "bold",
    height: '3em',
    marginBottom: '4px',
    float: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    alignContent: 'center',
    fontSize:'16px',
    minWidth: '162px',
});

const ValueGrid = styled(Grid)({
    float: 'left',
    height: '3em',
    marginLeft: '20px',
    alignContent: 'center',
});


const WorkItemCloneDrawer = (props: WorkItemCloneProps) => {
    const [cloneWorkItemId, setCloneWorkItemId] = useState<number>(props.workItemId);
    const [numberOfCopies, setNumberOfCopies] = useState('');
    const [cloneWorkItem, setCloneWorkItem] = useState<string>('');
    const [suffix, setSuffix] = useState('');
    const [startingAt, setStartingAt] = useState('');
    const [copies, setCopies] = useState<Array<string>>([]);
    const [errors, setErrors] = useState<boolean[]>([]);
    const minNumberOfCopies = 1;
    const maxNumberOfCopies = 99;
    const { displaySnackbar } = useSnackbar();
    const originalCloneWorkitemName = props.workItemName;

    useEffect(() => {
        if (props.workItemName)
            setCloneWorkItem(props.workItemName);

        if (props.workItemId)
            setCloneWorkItemId(props.workItemId);

    }, [props])

    useEffect(() => {
        const newErrors = copies.map((copy, index) => handleError(copy, index));
        setErrors(newErrors);
    }, [copies]);

    const hasErrors = errors.some((error) => error);

    const [handleCloneWorkItem, cloneWorking] = useCloneWorkItem();

    const isNumeric = (str: string) => /^\d+$/.test(str);
    const isAlphabetic = (str: string) => /^[a-zA-Z]+$/.test(str);

    const prepareClones = () => {
        const numCopies = parseInt(numberOfCopies, 10);
        const mainPart = startingAt.slice(0, -1);
        const lastChar = startingAt.slice(-1);

        let newCopies = [] as Array<string>;

        const base = `${cloneWorkItem} ${suffix ? suffix : ''}`.trim();
        if (base.length > 255) {
            displaySnackbar('error', 'Maximum length of 255 characters exceeded - please edit')
            setErrors([true]);
        }
        else {
            const generateCopy = (index: number) => {
                if (startingAt) {
                    if (isNumeric(lastChar)) {
                        const startNum = parseInt(lastChar, 10);
                        return `${base} ${mainPart}${startNum + index}`.trim();
                    } else if (isAlphabetic(lastChar)) {
                        const startChar = lastChar.charCodeAt(0);
                        return `${base} ${mainPart}${String.fromCharCode(startChar + index)}`.trim();
                    }
                } else {
                    return base
                }

                return base;
            };

            for (let i = 0; i < numCopies; i++) {
                newCopies.push(generateCopy(i));
            }

            setCopies(newCopies);
        }
    };

    const handleCopyChange = (index: number, newValue: string) => {
        const updatedCopies = [...copies];
        updatedCopies[index] = newValue;
        setCopies(updatedCopies);
    };

    const handleNumberOfCopiesChange = (value: string) => {
        const numericValue = Number(value);

        if (value != '' && numericValue > maxNumberOfCopies) {
            value = maxNumberOfCopies.toString();
        }
        if (value != '' && numericValue < minNumberOfCopies) {
            value = minNumberOfCopies.toString();
        }

        setNumberOfCopies(value)
    }

    const handleSave = async (cloneWorkItemId: number, copies: Array<string>)  => {
        await handleCloneWorkItem({ workItemId: cloneWorkItemId, copies: copies });
        handleCloseDrawer();
    }

    const handleError = (copy: string, index: number) => {
        if (copy === '' || copy === originalCloneWorkitemName) {
            return true;
        }
        for (let i = 0; i < copies.length; i++) {
            if (i !== index && copies[i] === copy) {
                return true;
            }
        }
        if(copy.length > 255){
            return true;
        }
        return false;
    };

    const getHelperText = (copy: string, index: number) => {
        if (copy === '') {
            return 'Work item name is required.';
        }
        if (copy === originalCloneWorkitemName) {
            return `${copy} already exists.`;
        }
        for (let i = 0; i < copies.length; i++) {
            if (i !== index && copies[i] === copy) {
                return `${copy} already exists.`;
            }
        }
        if(copy.length > 255){
            return 'Allows maximum 255 characters'
        }
        return null;
    };    

    const handleCloseDrawer = () => {
        setCloneWorkItemId(props.workItemId);
        setNumberOfCopies('');
        setCloneWorkItem(props.workItemName);
        setSuffix('');
        setStartingAt('');
        setCopies([]);
        setErrors([]);
        props.setOpenCloneWorkitem(false)
    }

    const handleDelete = (index: number) => {
        const updatedCopies = copies.filter((_, i) => i !== index);
        setCopies(updatedCopies);
    };    

    return (
        <Box>
            <Drawer
                anchor={'right'}
                open={props.openCloneWorkitem}
                onClose={() => handleCloseDrawer()}
                PaperProps={{
                    sx: {
                        width: '40%',
                        backgroundColor: "rgba(255, 255, 255, 1)"
                    }
                }}
                sx={{ zIndex: 1300 }}
            >
                <DrawerHeader title={
                    <PageTitle sx={{ color: '#001A69', ml: '-0.5vw', mb: '24px' }}>
                        Clone Work Item (including Workflows)
                    </PageTitle>
                } onClose={() => handleCloseDrawer()} />
                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box>
                                <Grid container spacing={1} sx={{ marginBottom: '16px' }}>
                                    <KeyGrid>Number of copies to be created:</KeyGrid>
                                    <ValueGrid xs>
                                        <TextField
                                            sx={{ minWidth: '70px', width: '70px' }}
                                            id="number-of-copies"
                                            type='number'
                                            value={numberOfCopies}
                                            onChange={(event) => handleNumberOfCopiesChange(event.target.value)}
                                            InputProps={{ inputProps: { min: minNumberOfCopies, max: maxNumberOfCopies } }} />
                                    </ValueGrid>
                                </Grid>
                                <Grid container spacing={1} sx={{ marginBottom: '16px' }}>
                                    <KeyGrid xs={3}>Clone Work Item ({numberOfCopies}):</KeyGrid>
                                    <ValueGrid xs>
                                        <TextField
                                            id="number-of-copies"
                                            value={cloneWorkItem}
                                            fullWidth
                                            onChange={(event) => setCloneWorkItem(event.target.value)} />
                                    </ValueGrid>
                                </Grid>
                                <Grid container spacing={1} sx={{ marginBottom: '16px' }}>
                                    <KeyGrid xs={3}>Suffix:</KeyGrid>
                                    <ValueGrid xs>
                                        <TextField
                                            id="number-of-copies"
                                            value={suffix}
                                            fullWidth
                                            onChange={(event) => setSuffix(event.target.value)} />
                                    </ValueGrid>
                                </Grid>
                                <Grid container spacing={1} sx={{ marginBottom: '16px' }}>
                                    <KeyGrid xs={3}>Starting at:</KeyGrid>
                                    <ValueGrid xs>
                                        <TextField
                                            id="number-of-copies"
                                            value={startingAt}
                                            fullWidth
                                            onChange={(event) => setStartingAt(event.target.value)} />
                                    </ValueGrid>
                                </Grid>
                                <Grid container spacing={1} sx={{paddingTop: '24px',gap: '16px', paddingBottom:'24px'}}>
                                    <Grid xs={6}>
                                        
                                    </Grid>
                                    <Grid xs>
                                        <Button fullWidth variant="contained" sx={{ textTransform: 'none' }} disabled={numberOfCopies == '' || numberOfCopies == '0'} onClick={prepareClones}>
                                            {'Prepare Clone(s)'}
                                        </Button>
                                    </Grid>
                                </Grid>
                                {
                                    copies.map((copy, index) => (
                                        <Grid container spacing={1} marginTop={1} key={index} alignItems="center">
                                            <Grid item xs>
                                                <TextField
                                                    value={copy}
                                                    fullWidth
                                                    error={handleError(copy, index)}
                                                    helperText={getHelperText(copy, index)}
                                                    onChange={(event) => handleCopyChange(index, event.target.value)}
                                                />
                                            </Grid>
                                            <Grid sx={{alignSelf: 'start', mt:'10px'}} item>
                                                <Ico_Delete 
                                                    sx={{ color: "#001A69", cursor: 'pointer', height: '20px', width: '20px ' }} 
                                                    onClick={() => handleDelete(index)} 
                                                />
                                            </Grid>
                                        </Grid>
                                    ))
                                }
                                <Grid container spacing={1} sx={{paddingTop: '24px', gap: '16px'}}>
                                    <Grid xs={6}>
                                        <Button fullWidth variant="outlined" onClick={() => handleCloseDrawer()}>
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid xs>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            sx={{ textTransform: 'none' }}
                                            disabled={copies.length === 0 || hasErrors}
                                            endIcon={
                                                cloneWorking ? (
                                                    <CircularProgress
                                                        size={20}
                                                        sx={{
                                                            position: 'unset',
                                                            color: (theme) => theme.palette.primary.contrastText,
                                                        }}
                                                    />
                                                ) : null
                                            }
                                            onClick={() => { handleSave(cloneWorkItemId, copies)}}
                                        >
                                            Create Clone(s)
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </>
            </Drawer>
        </Box>
    )
};

export default WorkItemCloneDrawer;